export const chartSeries = {
    adminLlmUsage: [
        "#1976d2",
        "#9c27b0",
        "#2e7d32",
        "#ed6c02",
        "#0288d1",
        "#d32f2f",
        "#7b1fa2",
        "#558b2f",
        "#c2185b",
        "#00796b",
    ],
    controlTower: ["#2563eb", "#dc2626", "#7ca1f3"],
    controlTowerAge: ["#93c5fd", "#60a5fa", "#3b82f6", "#1e40af"],
    /** A drawing's change level in the revision narrative: minor, moderate, major. */
    revisionNarrative: ["#fbbf24", "#f97316", "#dc2626"],
    schedule: ["#4dabf7", "#fab005", "#40c057", "#fa5252", "#9c27b0", "#666666"],
    /**
     * The part lines of the connection-health timeline (at most six, one hue each, in
     * fixed order): blue, orange, aqua, yellow, magenta, violet. The verdict band beside
     * them draws the status palette through Joy's semantic variables, so no series hue
     * is a status green, amber or red.
     */
    connectionHealth: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#9085e9"],
    constructability: [
        { base: "#60a5fa", hover: "#2563eb" },
        { base: "#34d399", hover: "#059669" },
        { base: "#fb923c", hover: "#ea580c" },
        { base: "#f87171", hover: "#dc2626" },
        { base: "#c084fc", hover: "#9333ea" },
        { base: "#f472b6", hover: "#db2777" },
        { base: "#94a3b8", hover: "#475569" },
    ],
    /**
     * The admin console's Pipeline tab: an attempt's outcome (the run flame, the outcome bars,
     * the waste chart). An open attempt is the theme's primary, not a series hue.
     */
    pipeline: {
        committed: "#5ec997",
        failed: "#ef6b67",
        fenced: "#e7a83e",
        crashed: "#9085e9",
    },
    usage: {
        /**
         * Usage's lifecycle, in the standard colours (hp, Nov 2): retained blue, new green,
         * resurrected the lighter aqua-teal, churned red, from the dataviz categorical steps (blue,
         * green, aqua, red); the retention chart's new-people line is the new green. Dark keeps the
         * aqua's light step, since its dark step sat 11.9 from the green. Validated in stack order
         * (churned, retained, resurrected, new) with validate_palette.js: light passes, worst
         * adjacent ΔE 15.6 normal / 15.2 deutan, aqua 2.74:1 on the surface (relief: the direct
         * end labels and the hover); dark passes every check, worst adjacent ΔE 15.6 / 15.2.
         */
        lifecycle: {
            light: {
                retained: "#2a78d6",
                resurrected: "#1baf7a",
                new: "#008300",
                churned: "#e34948",
            },
            dark: {
                retained: "#3987e5",
                resurrected: "#1baf7a",
                new: "#008300",
                churned: "#e66767",
            },
        },
        /**
         * The cohort layer cake's layers (item 19), one blue ramp, oldest start month darkest
         * (index 0) to newest lightest; fewer layers take evenly spread steps. Interpolated in
         * OKLab between the dataviz blue steps 700 and 250 (light) and 600 and 100 (dark) so six
         * steps clear the ordinal gap. validate_palette.js --ordinal: all checks pass in both
         * schemes (light end 2.11:1 on white, dark end 2.19:1 on the dark surface).
         */
        layers: {
            light: ["#0d366b", "#254e84", "#3c679e", "#5480b8", "#6d9bd3", "#86b6ef"],
            dark: ["#184f95", "#3d6caa", "#6089be", "#84a6d3", "#a8c4e7", "#cde2fb"],
        },
        /**
         * One line per device kind on the Devices trend, in the categorical order (blue, orange,
         * aqua, yellow, magenta) and fixed per kind, so a device filter never repaints the rest.
         * Validated: all checks pass in both schemes; three light slots sit under 3:1, so the
         * chart labels every line at its end and in the legend.
         */
        devices: {
            light: {
                web: "#2a78d6",
                ios_app: "#eb6834",
                ipad_browser: "#1baf7a",
                embed: "#eda100",
                desktop: "#e87ba4",
            },
            dark: {
                web: "#3987e5",
                ios_app: "#d95926",
                ipad_browser: "#199e70",
                embed: "#c98500",
                desktop: "#d55181",
            },
        },
    },
} as const;

/** Exact content colors from the tenant-specific Quality Dashboard's source Smartsheet. */
export const qualityDashboardPalette = {
    inverseInk: "#ffffff",
    companyLines: [
        "#1e40af",
        "#c62828",
        "#2e7d32",
        "#b45309",
        "#6a0dad",
        "#0891b2",
        "#be185d",
        "#4d7c0f",
        "#7c2d12",
        "#334155",
        "#0369a1",
        "#a16207",
        "#15803d",
        "#9333ea",
        "#db2777",
        // Warm grey, not the teal-green it replaced: from index 8 the lines are
        // dashed, and a third dashed green beside #15803d did not separate.
        "#57534e",
    ],
    pass: "#2e7d32",
    fail: "#c62828",
    rate: "#1e40af",
    revision: "#6a0dad",
    openLine: "#000000",
    neutral: "#64748b",
    accent: "#0369a1",
    ink: "#1f2a5a",
    tileHeader: "#e6ecfa",
    // HBW's dashboard header, from their mock-up: the full-width bar under the
    // title, and the white plate the header sits on in both themes (the title
    // is navy and the logo black, so neither survives a dark surface).
    heroBar: "#3d7d2f",
    heroSurface: "#ffffff",
    failedOpen: "#a16207",
    open: "#ea580c",
    contractorNew: "#0891b2",
    qualityControlNew: "#7c3aed",
    groups: {
        Tower: "#6aa84f",
        HMR: "#f7e463",
        SMR: "#000000",
    },
    fallbackGroups: ["#b6d7a8", "#ffe599", "#a4c2f4", "#d5a6bd", "#b4a7d6"],
} as const;

/** Stable hues for the preconstruction category dimension in stacked charts. */
export const preconCategoryPalette = {
    drawingSpecs: "#2563eb",
    crossDiscipline: "#0f766e",
    equipmentSchedules: "#b45309",
    drawingTags: "#64748b",
} as const;

export const avatarPalette = [
    "#93C5FD",
    "#BFDBFE",
    "#A5F3FC",
    "#A7F3D0",
    "#FCD34D",
    "#FDBA74",
    "#FCA5A5",
    "#F9A8D4",
    "#C4B5FD",
    "#CBD5E1",
] as const;

export const annotationPalette = [
    ["#E53935", "red"],
    ["#2563eb", "blue"],
    ["#43A047", "green"],
    ["#FB8C00", "orange"],
    ["#FFEB3B", "yellow"],
    ["#8E24AA", "purple"],
    ["#000000", "black"],
] as const;

/** Named lookup for the viewer's markup colour picker and renderer. */
export const annotationColor = {
    red: annotationPalette[0][0],
    blue: annotationPalette[1][0],
    green: annotationPalette[2][0],
    orange: annotationPalette[3][0],
    yellow: annotationPalette[4][0],
    purple: annotationPalette[5][0],
    black: annotationPalette[6][0],
} as const;

/** Viewer markers that identify review tools and their states. */
export const viewerMarkerPalette = {
    askRegion: "#d32f2f",
    clash: "#d97706",
    clashOutline: "#0f172a",
    rfi: "#2563eb",
    issue: "#9a5b13",
    flag: "#f97316",
    dismissedFlag: "#fdc99b",
    link: "#1976d2",
    redlineAdd: "#2b8a3e",
    redlineDelete: "#c92a2a",
    redlineModify: "#b7791f",
    redlineDefault: "#3b5bdb",
    connectionWarning: "#ff0000",
    /** The key row of a defining source, outlined on the destination dialog's page image. */
    definitionRow: "#eab308",
} as const;

/** Viewer colours that describe detected components on a sheet. */
export const viewerComponentPalette = {
    drawingDetail: "#F44336",
    table: "#4CAF50",
    notes: "#2196F3",
    default: "#9E9E9E",
    structuredTable: "#FFD600",
    legend: "#00BCD4",
    warning: "#ED6C02",
    cyan: "#00BCD4",
    orange: "#FF9800",
    purple: "#9C27B0",
    yellow: "#FFC107",
    tie: "#D14343",
    verdictAccepted: "#16a34a",
    verdictRejected: "#9ca3af",
} as const;

/** Viewer colours used to connect and distinguish drawn destinations. */
export const viewerConnectionPalette = {
    resolved: "#00b400",
    unresolved: "#3b82f6",
    noDestination: "#ef4444",
    partial: "#ed6c02",
    label: "#0f172a",
    edge: "#94a3b8",
    surface: "#e2e8f0",
    active: "#1565c0",
    destination: "#d20000",
    match: "#1a8a1a",
    neutral: "#808080",
    quiet: "#c0c0c0",
    floating: "#9fa6ad",
} as const;

/** Stable crop hues that distinguish clustered destination candidates. */
export const destinationCropPalette = [
    "#78b4ff",
    "#ffa078",
    "#96dc96",
    "#e696e6",
    "#f0dc78",
    "#8cdcdc",
    "#ff8caa",
    "#b4b4ff",
    "#c8e68c",
    "#ffc88c",
    "#a0c8ff",
    "#dca0c8",
] as const;

/** Viewer text-annotation colours for fields, guides, and handles. */
export const viewerTextPalette = {
    cyan: "#00FFFF",
    magenta: "#FF00FF",
    blue: "#0000FF",
    pink: "#FFB6C1",
    selection: "#facc15",
    selectionWash: "#fff0a6",
    selectionText: "#6f5200",
} as const;

/** Viewer markup rendering colours outside the editable annotation picker. */
export const viewerMarkupPalette = {
    white: "#FFFFFF",
    black: "#000000",
    graphite: "#111827",
    fill: "#F3F4F6",
    amber: "#F59E0B",
    snap: "#E91E63",
    success: "#15803D",
    danger: "#B91C1C",
    primary: "#1D4ED8",
    neutral: "#6B7280",
    /** Marvin's markup blue: the sheet and the conversation card's preview draw a Marvin markup in it. */
    marvin: "#2563EB",
} as const;

/**
 * Marvin's mark (canvas page M): a white M with a blue glow in a blue wireframe on dark surfaces,
 * a brand-blue M in a fainter wireframe on light ones, amber while he waits on the user, and the
 * launcher's navy disc. `MarvinMark` draws with these and nothing else.
 */
export const marvinMarkPalette = {
    dark: {
        shell: "#5B7CFF",
        sphere: "#7F9BFF",
        arc: "#B9CCFF",
        tone: "#5B7CFF",
        ink: "#FFFFFF",
        amber: "#F5B83D",
    },
    light: { wire: "#2563EB", tone: "#2563EB", amber: "#B45309" },
    launcher: { centre: "#1E3A8A", edge: "#0D1322" },
} as const;

/** File comparison colours that identify the source of each change band. */
export const fileComparisonPalette = {
    added: "#7b4fd0",
    removed: "#ea580c",
} as const;

/** Admin job states and chart annotations that need stable status colours. */
export const adminStatusPalette = {
    blue: "#3b82f6",
    blueDark: "#1d4ed8",
    blueLight: "#93c5fd",
    green: "#10b981",
    greenDark: "#2e7d32",
    red: "#ef4444",
    redDark: "#d32f2f",
    redWash: "#fef2f2",
    amber: "#f59e0b",
    amberDark: "#b45309",
    missing: "#ed6c02",
    amberWash: "#fef3c7",
    purple: "#ad1457",
    neutral: "#9ca3af",
    neutralDark: "#6b7280",
    neutralLight: "#d1d5db",
    neutralMid: "#bdbdbd",
    inactive: "#9e9e9e",
    running: "#0288d1",
    queued: "#64b5f6",
    indigoWash: "#e0e7ff",
    neutralWash: "#f3f4f6",
    successWash: "#ecfdf5",
    primaryWash: "#eff6ff",
    warningWash: "#fffbeb",
} as const;

/** Admin diagnostic views that preserve source-control syntax colours. */
export const adminDiagnosticPalette = {
    link: "#0550ae",
    linkActive: "#0a3069",
    danger: "#cf222e",
    muted: "#6e7781",
    text: "#24292f",
    border: "#57606a",
} as const;

/** Admin email previews use these document colours outside the app chrome. */
export const adminEmailPalette = {
    border: "#e0e0e0",
    text: "#1a1a1a",
    muted: "#666",
    link: "#2563eb",
    white: "#FFFFFF",
    surface: "#f3f4f6",
    heading: "#1f2937",
    divider: "#e5e7eb",
    tertiary: "#9ca3af",
    panelBorder: "#cdd7e1",
} as const;

/** Schedule bars and canvas annotations preserve their established project colours. */
export const schedulePalette = {
    notStarted: "#4dabf7",
    inProgress: "#fab005",
    complete: "#40c057",
    critical: "#fa5252",
    milestone: "#9c27b0",
    wbsSummary: "#666666",
    highlight: "#FFD600",
    selected: "#1976d2",
    // The Gantt is rendered in SVG, so use the active Joy variables rather than
    // fixed paper colours. This keeps the canvas aligned with both app schemes.
    gridLine: "var(--joy-palette-divider)",
    textPrimary: "var(--joy-palette-text-primary)",
    textSecondary: "var(--joy-palette-text-secondary)",
    headerBg: "var(--joy-palette-background-surface)",
    yearHeaderBg: "var(--joy-palette-background-level1)",
    yearHeaderBorder: "var(--joy-palette-divider)",
    relationshipLine: "var(--joy-palette-text-tertiary)",
    white: "#FFFFFF",
} as const;

/** Admin grid direction previews keep a fixed canvas surface and guide colour. */
export const adminCanvasPalette = {
    surface: "#f8f9fa",
    grid: "#e0e0e0",
    white: "#ffffff",
} as const;

/** Console tag colours for `PrimeDebugLog`: one per hashed tag, `text` on each. */
export const debugTagPalette = {
    text: "#FFF",
    tags: [
        "#FF5733", // Red-ish
        "#3498db", // Blue
        "#e67e22", // Orange
        "#e74c3c", // Bright Red
        "#95a5a6", // Grey
        "#2ecc71", // Green
        "#9b59b6", // Purple
        "#34495e", // Dark Blue
    ],
} as const;

/** A paper-coloured sheet thumbnail drawn on a canvas (style lab fixtures): the
 *  paper surface, its frame, the faint rule lines and the ink of the sheet code. */
export const paperThumbnailPalette = {
    paper: "#f2f4f7",
    frame: "#c9d0da",
    rule: "#dfe4eb",
    ink: "#26313d",
} as const;

/** The search-hit `<mark>` behind matched terms in titles, rows and answers. */
export const searchHighlightPalette = {
    mark: "#fef08a",
} as const;

/** The login-background preview in the customization modal: the canvas the tenant's
 *  image is composed on, independent of the app scheme. */
export const customizationPreviewPalette = {
    canvas: "#fdfefe",
} as const;

/** The drawings sidebar's annotation-pass marks: one glyph colour per sheet review state. */
export const annotationMarkPalette = {
    notExtracted: "#bbb",
    needsWork: "#999",
    inProgress: "#e8850c",
    reviewed: "#189454",
} as const;

/** Control Tower KPI sparklines: the "good" tone beside `chartSeries.controlTower`. */
export const controlTowerSparklinePalette = {
    good: "#16a34a",
} as const;

/** The mock Procore host page (`/embed-test`) imitates Procore's own chrome. */
export const embedMockHostPalette = {
    page: "#f5f5f5",
    header: "#2e5266",
    headerAccent: "#f68b1e",
    muted: "#666",
    frameBorder: "#ddd",
    note: "#f0f0f0",
} as const;

/** The overlay viewer's canvas and SVG filters: paper white and ink black. */
export const overlayCanvasPalette = {
    paper: "#fff",
    ink: "#000",
} as const;

/** The rendered PDF page: the paper under every page canvas (the embedpdf page wrappers,
 *  the thumbnail downsample) and the letterbox's last resort without the theme. */
export const pdfPagePalette = {
    paper: "#fff",
} as const;

/** The feedback screenshot: the DOM rasterizer paints this under transparent regions of
 *  the captured window. */
export const feedbackCapturePalette = {
    backdrop: "#fff",
} as const;

/** The standalone viewer-compare perf harness mounts the PDF engine with no theme; the
 *  letterbox around its pages is this fixed neutral. */
export const viewerComparePalette = {
    letterbox: "#e8e8e8",
} as const;

/** The review stamp's cover page, which the server renders into a PDF: document ink, not UI
 *  colour. `accent` is a new template's default accent; each template may change its own. */
export const reviewStampPalette = {
    accent: "#3c5668",
} as const;

/** The constructability review page's admin layer (review bar, verdict buttons, grouping
 *  row, admin facets): a purple accent that marks admin-only controls apart from the
 *  primary blue. Scoped to that page by `crAdminScope` in shared/styles. `light` is the
 *  pre-Design-v2 purple; `dark` keeps the hue and lifts it to read on the dark chrome. */
export const crAdminPalette = {
    light: { accent: "#7e57c2", softBackground: "#f5f0fa", border: "#d9c8ef" },
    dark: {
        accent: "#9575cd",
        softBackground: "rgba(126, 87, 194, 0.16)",
        border: "rgba(126, 87, 194, 0.4)",
    },
} as const;

/** Project Timeline scene (WebGL and canvas overlay): one hue pair per lane, [dark, light]
 *  (the light one is deeper, to hold its own on a white floor), the fallback pairs for a
 *  lane the client does not know, the neutral hue of an unknown lane, and the ink of the
 *  number disc by scheme. A theme may override any lane with `--timeline-lane-<key>`. */
export const timelineLanePalette = {
    lanes: {
        contract: ["#b69cff", "#6d4fd6"],
        notice: ["#ff9254", "#d9560f"],
        meeting: ["#5c9dff", "#1f63d6"],
        correspondence: ["#3dd6cc", "#0b8f87"],
        position: ["#ff6f91", "#d12e58"],
        cost: ["#ffd166", "#b07d00"],
        field: ["#7ee08a", "#2a9a3c"],
        design: ["#9fd8ff", "#1c7fb8"],
        rfi: ["#c0e35b", "#6c8e00"],
        submittal: ["#e58cff", "#a62ec2"],
        transmittal: ["#d4b896", "#8a6a3f"],
        daily_report: ["#8da2fb", "#4a5fd1"],
        issue: ["#ff6b6b", "#c92a2a"],
        schedule: ["#a9b4c4", "#5b6678"],
    },
    fallback: [
        ["#f5a6e6", "#b13c9b"],
        ["#a6e3c1", "#2f8f5d"],
        ["#f0c987", "#9a6a14"],
        ["#9fb7e8", "#3b5fa8"],
    ],
    unknown: "#9aa3b2",
    discInk: { dark: "#0b0e15", light: "#ffffff" },
    /** A change order's chain: the 3D arc and the line between its steps, dark and light. */
    chain: ["#ffe2a8", "#b07000"],
} as const;

/** Drawings' 3D view (`drawings3d/palette.ts`): its scene is always drawn light on a dark canvas
 *  (the ink is additive), in both themes, so these are fixed values and not theme tokens. */
export const drawings3dScenePalette = {
    /** The canvas behind the building: a radial wash from `night2` at the middle to `night`. */
    night: "#07111b",
    night2: "#0d1d2b",
    /** Architectural ink, frames and labels. */
    vellum: "#e4ebf1",
    dim: "#93a8b9",
    faint: "#5b7285",
    /** Gridlines and their bubbles. */
    redline: "#ff6b4a",
    /** An unconfirmed placement's outline (the warning tone on a dark canvas). */
    flag: "#f2b84b",
    /** A section's ink, whatever its discipline. */
    section: "#ffb59a",
    /** The 3D surround's paper look over the page: its ink, the selected drawing's outline (the
     *  viewer's selection blue) and the other drawings' outlines. */
    paperInk: "#1b2330",
    paperSelected: "#0b6bcb",
    paperFaint: "#8a95a5",
    /** Ink per discipline family; architectural is `vellum`. */
    disciplines: {
        S: "#e8b46c",
        M: "#5fd3e6",
        P: "#7bdc8f",
        FP: "#ff8fc4",
        FA: "#b8e986",
        E: "#f5dc5a",
        EL: "#fff0a6",
        T: "#8fb3ff",
        K: "#c6a2ff",
    },
} as const;

/** The ontology inspector's kind colours: the panel's swatch and the box on the sheet. No red
 *  (late) or amber (waiting); a kind not named takes a fallback by its name's hash. */
export const ontologyKindPalette = {
    kinds: {
        door: "#2f6fec",
        room: "#13915a",
        level: "#7b4fd6",
        hardware_set: "#0d8aa1",
        equipment: "#c05a12",
        panel: "#b0367d",
        circuit: "#5b6b82",
        air_terminal: "#2f6fec",
        light_fixture: "#8a6d00",
        sprinkler_head: "#0d8aa1",
        column: "#5b6b82",
        grid_axis: "#7b4fd6",
        wall_opening: "#c05a12",
    },
    fallback: ["#2f6fec", "#13915a", "#7b4fd6", "#c05a12", "#b0367d", "#0d8aa1"],
    /** The halo under the focused entity's outline, so it reads on any drawing. */
    focusHalo: "rgba(255, 255, 255, 0.95)",
} as const;
